/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable max-len */
/* eslint-disable no-unused-expressions */
import { EmailUser, Product } from 'hydra-data-types';
import { ConnectInitializeUser } from '../../store/StoreTypes';
import { Environment } from '../Environment';
import { Errors } from '../Errors';
import { AnalyticsEvent, AnalyticsEventName } from './AnalyticsEvent';

const WRITE_KEYS = {
  // https://app.segment.com/mindset-health/sources/hcp_portal_dev/settings/keys
  Development: 'wYb52ATETy6ucmD8FLqN4tFQjDzPCYTz',
  // https://app.segment.com/mindset-health/sources/hcp_portal/settings/keys
  Production: 'QWeWyXsfbAE3Zwx7wmecLICY93hovgzy',
};

const SEGMENT_ANONYMOUS_ID_STORAGE_KEY = 'ajs_anonymous_id';

type EventProperties = {
  [key: string]: any;
  [index: number]: any;
};

const BASE_PROPERTIES: Readonly<EventProperties> = {
  product: Product.Connect,
};

// The Segment SDK is loaded from the <head> tag in index.html. It would be nice to use @segment/analytics.js-code as
// a package, but this was causing events not to be forwarded from Segment to destinations.
function segment(): SegmentAnalytics.AnalyticsJS | undefined {
  return window.analytics;
}

async function initialize() {
  // Call load here rather than at the point we embed the library (see index.html). This allows us to
  // configure based on the environment.
  // See https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/quickstart/
  segment()?.load(Environment.isProduction() ? WRITE_KEYS.Production : WRITE_KEYS.Development);
  segment()?.page();

  segment()?.group(Product.Connect);
}

function getAnonymousId(): string {
  try {
    const segmentAnonymousId = segment()?.user()?.anonymousId() ?? '';
    return segmentAnonymousId.length > 0 ? segmentAnonymousId : generateAnonymousId();
  } catch (error) {
    // Segment may throw here, depending on the browsers privacy blocking capabilities.
    Errors.capture(error, 'info');
    const storedAnonymousId = localStorage.getItem(SEGMENT_ANONYMOUS_ID_STORAGE_KEY);
    return storedAnonymousId ?? generateAnonymousId();
  }
}

function generateAnonymousId() {
  // FIXME: This is a temporary fix to get a sudo-unique anonymousId for the user when the browser
  // prevents the Segment library from loading the user.
  const newAnonymousId = `anon-${Date.now().toString(36) + Math.random().toString(36).substring(2)}`;
  console.log('Generated new analytics anonymousId', newAnonymousId);

  // Because Segment may not be loaded, we need to manually store the anonymousId in localStorage
  localStorage.setItem(SEGMENT_ANONYMOUS_ID_STORAGE_KEY, newAnonymousId);
  segment()?.setAnonymousId(newAnonymousId);

  return newAnonymousId;
}

type UserTraits = {
  email: string;
  hcpProduct: boolean;
  name?: string;
  lastName?: string;
  profession?: string;
  referralHash?: string;
};

function identify(user: ConnectInitializeUser & { hcpProduct?: boolean }) {
  const traits: Required<UserTraits> = {
    name: user.name,
    lastName: user.lastName,
    email: user.email,
    profession: user.profession,
    hcpProduct: user.hcpProduct ?? false,
    referralHash: user.referralHash,
  };

  identifyUser({
    userId: user.userId,
    traits,
    logMessage: '[Analytics Identify]',
  });
}

function identifyPatient(user: EmailUser & { hcpProduct?: boolean }) {
  const traits: Omit<UserTraits, 'referralHash' | 'profession'> = {
    name: user.name,
    lastName: user.lastName,
    email: user.email,
    hcpProduct: user.hcpProduct ?? false,
  };

  identifyUser({
    userId: user.userId,
    traits,
    logMessage: '[Analytics Identify Patient]',
  });
}

function identifyLoggedInUser(user: { userId: string; email: string } & { hcpProduct?: boolean }) {
  const traits: Pick<UserTraits, 'email' | 'hcpProduct'> = {
    email: user.email,
    hcpProduct: user.hcpProduct ?? false,
  };

  identifyUser({
    userId: user.userId,
    traits,
    logMessage: '[Analytics Identify Logged In User]',
  });
}

type IdentifyUserArgs<T extends UserTraits> = {
  userId: string;
  traits: T;
  logMessage: string;
};

function identifyUser<T extends UserTraits>(args: IdentifyUserArgs<T>) {
  const { userId, traits, logMessage } = args;
  if (!Environment.isProduction()) {
    console.log(logMessage, userId, traits);
  }
  segment()?.identify(userId, traits);
}

function track(event: AnalyticsEvent) {
  const { event: eventName, ...properties } = event;
  const allProperties = { ...BASE_PROPERTIES, ...properties };

  if (!Environment.isProduction()) {
    console.log('[Analytics Event]', eventName, allProperties);
  }
  segment()?.track(eventName, allProperties);
}

function page(name: string, properties?: EventProperties) {
  const allProperties = { ...BASE_PROPERTIES, ...properties };
  segment()?.page(undefined, name, allProperties);
}

export const Analytics = {
  getAnonymousId,
  initialize,
  identify,
  identifyPatient,
  identifyLoggedInUser,
  track,
  page,
  Event: AnalyticsEventName,
};
